import { useColorModeValue } from "@/components/ui/color-mode"
import { colors, effects } from "@/theme"
import {
  Button as ChakraButton,
  type ButtonProps as ChakraButtonProps,
} from "@chakra-ui/react"
import { forwardRef } from "react"

export interface VisionButtonProps extends Omit<ChakraButtonProps, "variant"> {
  variant?:
    | "brand"
    | "outlineWhite"
    | "ghost"
    | "dark"
    | "light"
    | "transparent"
  loading?: boolean
}

const getDarkStyles = () => ({
  brand: {
    bg: "linear-gradient(120deg, #34a1dd, #5bbce6)",
    color: "white",
    boxShadow: effects.shadows.button,
    fontSize: "sm",
    fontWeight: "bold",
    letterSpacing: "wider",
    textTransform: "uppercase" as const,
    _hover: {
      bg: "linear-gradient(120deg, #2d8ec5, #4aafe0)",
      transform: "translateY(-2px)",
      boxShadow: "0px 8px 24px rgba(52, 161, 221, 0.5)",
      _disabled: {
        bg: "linear-gradient(120deg, #34a1dd, #5bbce6)",
        transform: "none",
        boxShadow: effects.shadows.button,
      },
    },
    _active: {
      bg: "linear-gradient(120deg, #185b7e, #2d8ec5)",
      transform: "translateY(0px)",
    },
    _disabled: { opacity: 0.6, cursor: "not-allowed" },
  },
  outlineWhite: {
    bg: "transparent",
    color: "white",
    border: "1px solid",
    borderColor: "white",
    fontSize: "sm",
    fontWeight: "bold",
    letterSpacing: "wider",
    textTransform: "uppercase" as const,
    _hover: {
      bg: "whiteAlpha.100",
      borderColor: "white",
      transform: "translateY(-2px)",
    },
    _active: { bg: "whiteAlpha.200", transform: "translateY(0px)" },
  },
  ghost: {
    bg: "transparent",
    color: "white",
    fontSize: "sm",
    fontWeight: "medium",
    _hover: { bg: colors.glass.whiteAlpha[100] },
    _active: { bg: colors.glass.whiteAlpha[200] },
  },
  dark: {
    bg: colors.glass.dark,
    color: "white",
    backdropFilter: "blur(20px)",
    border: "1px solid",
    borderColor: colors.glass.whiteAlpha[200],
    fontSize: "sm",
    fontWeight: "bold",
    letterSpacing: "wider",
    textTransform: "uppercase" as const,
    _hover: { borderColor: colors.brand[200], transform: "translateY(-2px)" },
    _active: { transform: "translateY(0px)" },
  },
  light: {
    bg: "white",
    color: "gray.900",
    fontSize: "sm",
    fontWeight: "bold",
    letterSpacing: "wider",
    textTransform: "uppercase" as const,
    _hover: { bg: "gray.100", transform: "translateY(-2px)" },
    _active: { bg: "gray.200", transform: "translateY(0px)" },
  },
  transparent: {
    bg: "transparent",
    color: "white",
    fontWeight: "medium",
    borderRadius: "inherit",
    cursor: "pointer",
    _hover: { color: colors.brand[200] },
    _active: { color: colors.brand[300] },
    _focus: { boxShadow: "none" },
  },
})

const getLightStyles = () => ({
  brand: {
    bg: "linear-gradient(120deg, #34a1dd, #5bbce6)",
    color: "white",
    boxShadow: effects.shadows.button,
    fontSize: "sm",
    fontWeight: "bold",
    letterSpacing: "wider",
    textTransform: "uppercase" as const,
    _hover: {
      bg: "linear-gradient(120deg, #2d8ec5, #4aafe0)",
      transform: "translateY(-2px)",
      boxShadow: "0px 8px 24px rgba(52, 161, 221, 0.3)",
      _disabled: {
        bg: "linear-gradient(120deg, #34a1dd, #5bbce6)",
        transform: "none",
        boxShadow: effects.shadows.button,
      },
    },
    _active: {
      bg: "linear-gradient(120deg, #185b7e, #2d8ec5)",
      transform: "translateY(0px)",
    },
    _disabled: { opacity: 0.6, cursor: "not-allowed" },
  },
  outlineWhite: {
    bg: "transparent",
    color: "#1a202c",
    border: "1px solid",
    borderColor: "#E2E8F0",
    fontSize: "sm",
    fontWeight: "bold",
    letterSpacing: "wider",
    textTransform: "uppercase" as const,
    _hover: {
      bg: "#EDF2F7",
      borderColor: "#CBD5E0",
      transform: "translateY(-2px)",
    },
    _active: { bg: "#E2E8F0", transform: "translateY(0px)" },
  },
  ghost: {
    bg: "transparent",
    color: "#4A5568",
    fontSize: "sm",
    fontWeight: "medium",
    _hover: { bg: "rgba(0,0,0,0.04)", color: "#1a202c" },
    _active: { bg: "rgba(0,0,0,0.08)" },
  },
  dark: {
    bg: "#EDF2F7",
    color: "#1a202c",
    backdropFilter: "none",
    border: "1px solid",
    borderColor: "#E2E8F0",
    fontSize: "sm",
    fontWeight: "bold",
    letterSpacing: "wider",
    textTransform: "uppercase" as const,
    _hover: { borderColor: colors.brand[200], transform: "translateY(-2px)" },
    _active: { transform: "translateY(0px)" },
  },
  light: {
    bg: "white",
    color: "#1a202c",
    fontSize: "sm",
    fontWeight: "bold",
    letterSpacing: "wider",
    textTransform: "uppercase" as const,
    _hover: { bg: "#F7FAFC", transform: "translateY(-2px)" },
    _active: { bg: "#EDF2F7", transform: "translateY(0px)" },
  },
  transparent: {
    bg: "transparent",
    color: "#4A5568",
    fontWeight: "medium",
    borderRadius: "inherit",
    cursor: "pointer",
    _hover: { color: colors.brand[500] },
    _active: { color: colors.brand[600] },
    _focus: { boxShadow: "none" },
  },
})

export const VisionButton = forwardRef<HTMLButtonElement, VisionButtonProps>(
  ({ variant = "brand", loading, children, ...props }, ref) => {
    const allStyles = useColorModeValue(getLightStyles(), getDarkStyles())
    const styles = allStyles[variant] || allStyles.brand

    return (
      <ChakraButton
        ref={ref}
        borderRadius="999px"
        transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
        disabled={loading}
        _focus={{ boxShadow: "none" }}
        {...styles}
        {...props}
      >
        {children}
      </ChakraButton>
    )
  },
)

VisionButton.displayName = "VisionButton"
